/**
 * Operator-side waiting room — overlays the LiveKit video stage when
 * no field participant is connected yet. Shows a centred card with a
 * "Send reminder" button (re-fires the join-link email/SMS through
 * `POST /v1/sessions/{id}/remind`) and a soft procedural hold-music
 * loop that auto-starts when the operator's first user gesture
 * unlocks the audio context.
 *
 * Hold music is procedural (Web Audio OscillatorNode chord) rather
 * than an asset so we don't ship a licensed track, don't add bundle
 * weight, and don't fight CORS for a sample file. Volume is capped at
 * ~10% so it's audible-but-not-intrusive while the operator waits.
 *
 * Mounted inside OperatorRoom's LiveKitRoom so `useParticipants` is
 * available; auto-dismounts the music + overlay the moment a
 * `participant:*` identity (a field user) joins.
 */
import { useParticipants } from "@livekit/components-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { Session } from "@/lib/types";


type Props = {
  sessionId: string;
  /** Click handler for the "Invite field user" affordance — shown
   *  when no FIELD invite has been issued yet. SessionPage wires
   *  this to open the InviteFieldUserModal. */
  onInviteFieldUser?: () => void;
};

export function WaitingRoomOverlay({ sessionId, onInviteFieldUser }: Props) {
  const { t } = useTranslation();
  const participants = useParticipants();
  // Field/observer/auditor join with `participant:<uuid>`; operator
  // joins with `user:<id>`. We want the overlay to disappear the
  // moment ANY non-operator participant lands in the room.
  const hasOtherParticipant = participants.some((p) =>
    p.identity.startsWith("participant:"),
  );

  const [reminderResult, setReminderResult] = useState<
    | { kind: "idle" }
    | { kind: "ok"; channels: string[] }
    | { kind: "error"; detail: string }
  >({ kind: "idle" });

  // Whether the session has a pending FIELD invite (drives the CTA
  // wording: invite vs reminder). Shares the same cache key as
  // SessionPage's session query so this is a free read most of the
  // time. Refetches every 8s while the overlay is mounted so a newly-
  // issued invite from another tab flips the button without a manual
  // refresh.
  const sessionQuery = useQuery({
    queryKey: ["session", sessionId],
    queryFn: async () => {
      const res = await api.get<Session>(`/v1/sessions/${sessionId}`);
      return res.data;
    },
    refetchInterval: 8000,
    staleTime: 4000,
  });
  const hasPendingInvite = !!sessionQuery.data?.has_pending_field_invite;

  const remind = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ channels: string[] }>(
        `/v1/sessions/${sessionId}/remind`,
      );
      return res.data;
    },
    onSuccess: (data) => {
      setReminderResult({ kind: "ok", channels: data.channels });
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? t("ui.waitingRoom.reminderFailed");
      setReminderResult({ kind: "error", detail });
    },
  });

  // Hold music — only plays when the overlay is visible. We hook the
  // first pointerdown anywhere in the document to unlock the audio
  // context (iOS gate), then start the chord. Stops + cleans up the
  // moment the overlay dismounts.
  useHoldMusic(!hasOtherParticipant);

  if (hasOtherParticipant) return null;

  // ApiPaths surface — keep the path inline so we don't bloat the
  // helper for one call site.
  void ApiPaths;

  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="pointer-events-auto card max-w-md space-y-4 p-6 text-center">
        <div className="mx-auto h-12 w-12 animate-pulse rounded-full bg-nb-mint/30 ring-4 ring-nb-mint/40" />
        <div>
          <h2 className="text-lg font-semibold text-nb-text">
            {t("ui.waitingRoom.title")}
          </h2>
          <p className="mt-1 text-sm text-nb-text-2">
            {t("ui.waitingRoom.description")}
          </p>
        </div>
        {hasPendingInvite ? (
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => remind.mutate()}
            disabled={remind.isPending}
          >
            {remind.isPending
              ? t("ui.waitingRoom.sending")
              : t("ui.waitingRoom.sendReminder")}
          </button>
        ) : (
          // No FIELD invite issued yet (ad-hoc session, or the operator
          // ended/recreated the session and hasn't re-invited). Asking
          // to "remind" makes no sense here — there's nobody to remind.
          // Surface the invite flow instead.
          <button
            type="button"
            className="btn-primary w-full"
            onClick={onInviteFieldUser}
            disabled={!onInviteFieldUser}
          >
            {t("ui.waitingRoom.inviteFieldUser")}
          </button>
        )}
        {hasPendingInvite && reminderResult.kind === "ok" && (
          <p className="text-xs text-cvp-ok">
            {t("ui.waitingRoom.reminderSent", {
              channels:
                reminderResult.channels.join(" + ") ||
                t("ui.waitingRoom.noChannel"),
            })}
          </p>
        )}
        {hasPendingInvite && reminderResult.kind === "error" && (
          <p className="text-xs text-cvp-danger">{reminderResult.detail}</p>
        )}
      </div>
    </div>
  );
}


/** Hold-music loop using the bundled `/audio/wait-music.mp3` asset.
 *  Plays at ~30% volume on loop while the overlay is visible. Starts
 *  on the first user gesture in the document (browsers block autoplay
 *  until interaction); stops + cleans up when `active` flips false
 *  or the component unmounts. */
function useHoldMusic(active: boolean): void {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!active) {
      stopHoldMusic(audioRef);
      return;
    }

    let cancelled = false;

    const tryStart = () => {
      if (cancelled || audioRef.current) return;
      try {
        const audio = new Audio("/audio/wait-music.mp3");
        audio.loop = true;
        audio.volume = 0.3;
        audio.preload = "auto";
        // play() returns a Promise that rejects on autoplay block —
        // catch it so we can re-try after the next user gesture.
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            // Autoplay denied; we'll retry on the next gesture.
            if (!cancelled) audioRef.current = null;
          });
        }
        audioRef.current = audio;
      } catch {
        // Audio is best-effort; a denied element just means no music.
      }
    };

    // Try immediately (covers the case where audio was already unlocked
    // by an earlier interaction this page-load), then on first
    // pointerdown if the immediate attempt was blocked.
    tryStart();
    const onGesture = () => {
      tryStart();
    };
    window.addEventListener("pointerdown", onGesture);

    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", onGesture);
      stopHoldMusic(audioRef);
    };
  }, [active]);
}


function stopHoldMusic(
  audioRef: React.MutableRefObject<HTMLAudioElement | null>,
): void {
  const audio = audioRef.current;
  if (audio) {
    try {
      audio.pause();
      audio.src = "";
      audio.load();
    } catch {
      // Already torn down.
    }
  }
  audioRef.current = null;
}


